import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { interviewApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  ActionMenu,
  ActionMenuItem,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  Textarea,
  useToast,
} from '@/components/ui';
import {
  INTERVIEW_LABELS,
  INTERVIEW_TONES,
  MODE_LABELS,
  RESPONSE_LABELS,
  RESPONSE_TONES,
  hasInterviewStarted,
  interviewWhen,
  isStudentMissedInterview,
  whereToGo,
} from '@/features/interviews/status';
import { RescheduleRequestDialog } from '@/features/interviews/RescheduleRequestDialog';
import { RescheduleRequestPanel } from '@/features/interviews/RescheduleRequestPanel';
import { hasOpenRequest } from '@/features/interviews/reschedule';
import { studentInterviewActions } from '@/features/interviews/studentActions';
import { BlockedAction } from '@/features/applications/CandidateRestriction';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

/**
 * "Upcoming" is a status *and* a time. An interview stays SCHEDULED after its
 * slot has passed until the company closes it, so the status alone kept last
 * week's call on this tab as if it were still ahead.
 */
const TABS = [
  { key: 'SCHEDULED', label: 'Upcoming', upcoming: true },
  // Every closed outcome has its own tab, so none is reachable only via All.
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'CANCELLED', label: 'Cancelled' },
  { key: 'MISSED', label: 'Missed', missed: true },
  { key: '', label: 'All' },
];

const PAGE_SIZE = 20;

/** Tone of the icon tile: the interview's own status, as its badge reads. */
const TILE_TONES = {
  SCHEDULED: 'brand',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  NO_SHOW: 'danger',
};

/**
 * The stripe: the interview's status, except that an invitation still waiting
 * for the student's reply is the one thing on this page that needs them.
 */
function accentFor(interview) {
  if (hasOpenRequest(interview)) return 'warning';
  if (isStudentMissedInterview(interview)) return 'danger';
  if (interview.can_respond && interview.student_response === 'PENDING') return 'warning';
  return INTERVIEW_TONES[interview.status] ?? 'neutral';
}

// The meeting link is an external anchor, not a route, so it borrows the
// tinted main-action button's look (`tintBrand` `sm`) rather than being a <Button>.
const JOIN_CALL_CLASS =
  'border border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 ' +
  'rounded-input inline-flex min-h-8 max-w-full items-center ' +
  'justify-center gap-2 px-3 py-1 text-sm font-medium transition';

/**
 * A small calendar tile: weekday, day, month and start time. Muted once the
 * interview is over or called off, so upcoming ones stand out in a mixed list.
 */
function DateTile({ interview }) {
  const start = new Date(interview.scheduled_at);
  if (Number.isNaN(start.getTime())) return null;
  const upcoming = interview.status === 'SCHEDULED' && !hasInterviewStarted(interview);
  return (
    <div
      className={cn(
        'rounded-input w-[84px] shrink-0 overflow-hidden border text-center',
        upcoming ? 'border-brand-200' : 'border-line',
      )}
      aria-label={`On ${start.toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })}`}
    >
      <p
        className={cn(
          'py-0.5 text-[10px] font-semibold tracking-wide uppercase',
          upcoming ? 'bg-brand-600 text-white' : 'bg-surface-muted text-ink-muted',
        )}
      >
        {start.toLocaleDateString(undefined, { weekday: 'short' })}
      </p>
      <p
        className={cn(
          'pt-1 text-xl leading-none font-semibold',
          upcoming ? 'text-ink' : 'text-ink-muted',
        )}
      >
        {start.getDate()}
      </p>
      <p className="text-ink-muted text-[11px] uppercase">
        {start.toLocaleDateString(undefined, { month: 'short' })}
      </p>
      <p className="text-ink-muted border-line mt-1 border-t py-0.5 text-[11px] tabular-nums">
        {start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
      </p>
    </div>
  );
}

export function StudentInterviewsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('SCHEDULED');
  const [page, setPage] = useState(1);
  const [answering, setAnswering] = useState(null);
  const [message, setMessage] = useState('');
  const [requesting, setRequesting] = useState(null);

  const upcoming = TABS.find((tab) => tab.key === status)?.upcoming ?? false;
  const missed = TABS.find((tab) => tab.key === status)?.missed ?? false;
  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status && !missed ? { statuses: status } : {}),
    ...(upcoming ? { upcoming: true } : {}),
    ...(missed ? { missed: true } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['student-interviews', params],
    queryFn: () => interviewApi.listInterviews(params),
    // Switching tab keeps the list (and the tabs) on screen while the next
    // one loads.
    placeholderData: keepPreviousData,
  });

  const respond = useMutation({
    mutationFn: ({ id, response, message: note }) =>
      interviewApi.respondToInterview(id, response, note || null),
    onSuccess: (interview) => {
      toast.success(
        interview.student_response === 'ACCEPTED'
          ? 'Confirmed. Good luck!'
          : 'The company has been told you cannot make it.',
      );
      void queryClient.invalidateQueries({ queryKey: ['student-interviews'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      setAnswering(null);
      setMessage('');
    },
    onError: (respondError) =>
      toast.error(errorMessage(respondError, 'Could not send your answer')),
  });

  const withdraw = useMutation({
    mutationFn: (id) => interviewApi.withdrawRescheduleRequest(id),
    onSuccess: () => {
      toast.success('Request withdrawn. Your interview stays at its current time.');
      void queryClient.invalidateQueries({ queryKey: ['student-interviews'] });
    },
    onError: (withdrawError) =>
      toast.error(errorMessage(withdrawError, 'Could not withdraw the request')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const awaiting = rows.filter(
    (row) => row.can_respond && row.student_response === 'PENDING' && !hasOpenRequest(row),
  ).length;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader title="Interviews" description="Invitations from companies you applied to." />

      {awaiting > 0 && (
        <Alert tone="warning" className="mb-6" title="Waiting for your answer">
          {awaiting === 1
            ? 'One invitation still needs a reply.'
            : `${awaiting} invitations still need a reply.`}
        </Alert>
      )}

      {/* Loading and errors render inside the card, below the tabs, so
          switching tab never unmounts them. */}
      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Interviews"
            tabs={TABS}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading your interviews…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="calendar" size={22} />}
              title="No interviews yet"
              description="Companies invite you here once they shortlist your application."
              action={
                <LinkButton to={paths.student.applications} variant="secondary" size="sm">
                  See your applications
                </LinkButton>
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((interview) => {
            const joinUrl =
              interview.mode === 'VIDEO' && interview.meeting_url ? interview.meeting_url : null;
            const place = joinUrl ? null : whereToGo(interview);
            const can = studentInterviewActions(interview);
            const moved = interview.reschedule_count > 0 && interview.status === 'SCHEDULED';
            const displayStatus = isStudentMissedInterview(interview)
              ? 'NO_SHOW'
              : interview.status;
            return (
              <BoxRow
                key={interview.id}
                accent={accentFor(interview)}
                leading={<BoxAvatar icon="calendar" tone={TILE_TONES[displayStatus] ?? 'danger'} />}
                title={interview.title}
                badges={
                  <>
                    <Badge tone={INTERVIEW_TONES[displayStatus] ?? 'neutral'} dot>
                      {INTERVIEW_LABELS[displayStatus] ?? displayStatus}
                    </Badge>
                    {displayStatus === 'SCHEDULED' && !hasOpenRequest(interview) && (
                      <Badge tone={RESPONSE_TONES[interview.student_response] ?? 'neutral'} dot>
                        {RESPONSE_LABELS[interview.student_response]}
                      </Badge>
                    )}
                    {hasOpenRequest(interview) && (
                      <Badge tone="warning">
                        <Icon name="repeat" size={11} className="shrink-0" />
                        Reschedule requested
                      </Badge>
                    )}
                  </>
                }
                subtitle={
                  <span className="text-ink-muted">
                    For <span className="text-ink font-semibold">{interview.internship_title}</span>{' '}
                    at <span className="text-ink font-semibold">{interview.company_name}</span>
                  </span>
                }
                facts={[
                  { icon: 'clockOutline', text: interviewWhen(interview), tone: 'brand' },
                  { icon: 'chat', text: MODE_LABELS[interview.mode] ?? interview.mode },
                  { icon: 'flag', text: `Round ${interview.round_number}` },
                  place && { icon: 'company', text: place },
                  moved && {
                    icon: 'repeat',
                    text: `This slot has moved ${interview.reschedule_count} ${
                      interview.reschedule_count === 1 ? 'time' : 'times'
                    }`,
                    tone: 'warning',
                  },
                ]}
                // The when, at a glance, in the top-right: the one fact a
                // student scans a list of interviews for.
                corner={<DateTile interview={interview} />}
                meta={
                  interview.created_at && (
                    <span className="text-ink-muted text-xs">
                      Invited {formatDate(interview.created_at)}
                    </span>
                  )
                }
                actions={
                  <CardActions>
                    {/* While a reply is due, the reply and the call are the
                        three buttons; the application waits under "More". */}
                    {!interview.can_respond && (
                      <LinkButton
                        to={paths.student.applicationDetail(interview.application_id)}
                        variant="secondary"
                        size="sm"
                      >
                        <Icon name="fileText" size={14} />
                        View application
                      </LinkButton>
                    )}
                    {interview.can_respond && (
                      <ActionMenu label={`More actions for ${interview.title}`}>
                        <ActionMenuItem
                          icon="fileText"
                          to={paths.student.applicationDetail(interview.application_id)}
                        >
                          View application
                        </ActionMenuItem>
                        {can.withdraw.show && (
                          <ActionMenuItem
                            icon="reset"
                            tone="danger"
                            onSelect={() => withdraw.mutate(interview.id)}
                          >
                            Withdraw reschedule request
                          </ActionMenuItem>
                        )}
                        {can.request.show && (
                          <ActionMenuItem
                            icon="repeat"
                            disabled={can.request.disabled}
                            reason={can.request.title}
                            onSelect={() => setRequesting(interview)}
                          >
                            Request another time
                          </ActionMenuItem>
                        )}
                      </ActionMenu>
                    )}
                    {can.decline.show && (
                      <BlockedAction reason={can.decline.disabled ? can.decline.title : null}>
                        <Button
                          variant="tintDanger"
                          size="sm"
                          disabled={can.decline.disabled}
                          onClick={() => setAnswering(interview)}
                        >
                          <Icon name="close" size={14} />
                          {can.decline.label}
                        </Button>
                      </BlockedAction>
                    )}
                    {can.accept.show && (
                      <BlockedAction reason={can.accept.disabled ? can.accept.title : null}>
                        <Button
                          variant="tintPositive"
                          size="sm"
                          disabled={can.accept.disabled}
                          isLoading={respond.isPending && respond.variables?.id === interview.id}
                          onClick={() => respond.mutate({ id: interview.id, response: 'ACCEPTED' })}
                        >
                          <Icon name="check" size={14} />
                          {can.accept.label}
                        </Button>
                      </BlockedAction>
                    )}
                    {can.join.show && can.join.disabled && (
                      <BlockedAction reason={can.join.title}>
                        <Button variant="tintBrand" size="sm" disabled>
                          <Icon name="external" size={14} />
                          Join the call
                        </Button>
                      </BlockedAction>
                    )}
                    {can.join.show && !can.join.disabled && (
                      <a
                        href={can.join.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={JOIN_CALL_CLASS}
                      >
                        <Icon name="external" size={14} />
                        Join the call
                      </a>
                    )}
                  </CardActions>
                }
              >
                {/* Instructions are the point of the invitation, so unlike a
                    description they are never clamped. */}
                {interview.instructions && (
                  <p className="text-ink-muted mt-2.5 text-sm leading-6 whitespace-pre-line">
                    {interview.instructions}
                  </p>
                )}
                {interview.cancellation_reason && (
                  <p className="text-ink-muted mt-2 text-xs">
                    Cancelled: {interview.cancellation_reason}
                  </p>
                )}
                <RescheduleRequestPanel request={interview.reschedule_request} audience="student" />
              </BoxRow>
            );
          })}
        </BoxList>
      </ListCard>

      {requesting && (
        <RescheduleRequestDialog interview={requesting} onClose={() => setRequesting(null)} />
      )}

      <Modal
        open={Boolean(answering)}
        title="Decline the invitation"
        description="The company sees your message. Need a different time instead? Use “Request another time” in the More menu."
        onClose={() => setAnswering(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Message"
            rows={3}
            placeholder="I have an exam that morning — any afternoon that week works."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAnswering(null)}>
              Back
            </Button>
            <Button
              variant="danger"
              isLoading={respond.isPending}
              onClick={() =>
                respond.mutate({
                  id: answering.id,
                  response: 'DECLINED',
                  message: message.trim(),
                })
              }
            >
              Decline
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
